import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from './api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (token && savedUser) {
      try {
        setUser(JSON.parse(savedUser));
        // Verify token is still valid
        const res = await authAPI.profile();
        const freshUser = res.data;
        setUser(freshUser);
        localStorage.setItem('user', JSON.stringify(freshUser));
      } catch {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const login = async (email, password) => {
    const res = await authAPI.login({ email, password });
    const { token, user: userData } = res.data;
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  // Impersonation: super_admin swaps their own token for a target user's token.
  // The original credentials are stashed so they can be restored.
  const impersonate = async (impersonationToken, targetUser) => {
    const originalToken = localStorage.getItem('token');
    const originalUser = localStorage.getItem('user');
    localStorage.setItem('impersonation_backup_token', originalToken);
    localStorage.setItem('impersonation_backup_user', originalUser);
    localStorage.setItem('token', impersonationToken);
    localStorage.setItem('user', JSON.stringify(targetUser));
    setUser(targetUser);
  };

  const stopImpersonation = () => {
    const originalToken = localStorage.getItem('impersonation_backup_token');
    const originalUser = localStorage.getItem('impersonation_backup_user');
    if (!originalToken || !originalUser) return;
    localStorage.setItem('token', originalToken);
    localStorage.setItem('user', originalUser);
    localStorage.removeItem('impersonation_backup_token');
    localStorage.removeItem('impersonation_backup_user');
    setUser(JSON.parse(originalUser));
  };

  const isImpersonating = () => !!localStorage.getItem('impersonation_backup_token');

  const register = async (email, password, fullName, companyName) => {
    const res = await authAPI.register({ email, password, full_name: fullName, company_name: companyName });
    const { token, user: userData } = res.data;
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const res = await authAPI.profile();
      const freshUser = res.data;
      setUser(freshUser);
      localStorage.setItem('user', JSON.stringify(freshUser));
    } catch (err) { console.warn('[Auth] refreshUser failed (session may be invalid)', err); }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser, impersonate, stopImpersonation, isImpersonating }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
